import React, { useState, useEffect } from 'react';
import { Box, useMediaQuery, useTheme, Alert, AlertTitle, Typography } from '@mui/material';
import Sidebar from './Sidebar';
import Header from './Header';
import { Outlet } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const DashboardLayout = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isSuspended, setIsSuspended] = useState(false);

    useEffect(() => {
        const checkSuspension = () => {
            const userInfoStr = localStorage.getItem('userInfo');
            if (userInfoStr) {
                try {
                    const userInfo = JSON.parse(userInfoStr);
                    if (userInfo.tenant && userInfo.tenant.status === 'SUSPENDED') {
                        setIsSuspended(true);
                    } else {
                        setIsSuspended(false);
                    }
                } catch (e) {
                    console.error('Error parsing userInfo for suspension check', e);
                }
            }
        };

        checkSuspension();
        // Also check on interval if storage changes in other tabs
        const interval = setInterval(checkSuspension, 5000);
        return () => clearInterval(interval);
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            bgcolor: { xs: '#f0f2f5', md: '#f0f2f5' },
            p: { xs: 0, md: '20px' },
            position: 'relative',
            '@media print': {
                bgcolor: 'white',
                p: 0,
                m: 0
            }
        }}>
            {/* Responsive Sidebar */}
            <Box className="no-print">
                <Sidebar
                    mobileOpen={mobileOpen}
                    onClose={handleDrawerToggle}
                    isMobile={isMobile}
                />
            </Box>

            {/* Main Content Area */}
            <Box
                sx={{
                    flexGrow: 1,
                    ml: { xs: 0, md: '110px' }, // Space for fixed sidebar only on desktop
                    bgcolor: 'white',
                    borderRadius: { xs: 0, md: '32px' },
                    minHeight: { xs: '100vh', md: 'calc(100vh - 40px)' },
                    boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    '@media print': {
                        ml: 0,
                        borderRadius: 0,
                        boxShadow: 'none',
                        minHeight: 'auto'
                    }
                }}
            >
                {isSuspended && (
                    <Box sx={{ 
                        width: '100%', 
                        bgcolor: '#FFF5F5', 
                        borderBottom: '2px solid #FEB2B2',
                        p: 0,
                        zIndex: 1100,
                        className: "no-print"
                    }}>
                        <Alert 
                            severity="error" 
                            icon={<ErrorOutlineIcon sx={{ color: '#C53030' }} />}
                            sx={{ 
                                borderRadius: 0,
                                bgcolor: 'transparent',
                                color: '#9B2C2C',
                                '& .MuiAlert-message': { width: '100%' }
                            }}
                        >
                            <AlertTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Account Suspended</AlertTitle>
                            <Typography variant="body2">
                                Your school's account is currently <strong>SUSPENDED</strong>. Administrative features and portal access for all users are restricted. 
                                Please contact <strong>billing@squareedge.co.zw</strong> or your account manager to resolve this matter.
                            </Typography>
                        </Alert>
                    </Box>
                )}

                <Box sx={{ p: { xs: '16px', md: '24px 40px' }, flexGrow: 1, position: 'relative' }}>
                    {/* Global Header */}
                    <Box className="no-print">
                        <Header onMenuClick={handleDrawerToggle} />
                    </Box>

                    <Outlet context={{ handleDrawerToggle }} />
                </Box>
            </Box>
        </Box>
    );
};

export default DashboardLayout;
